import { Component, forwardRef, Input, OnDestroy, OnInit } from "@angular/core";
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  ValidatorFn,
} from "@angular/forms";
import { catchError, debounceTime, distinctUntilChanged, Observable, of, Subject, switchMap, takeUntil } from "rxjs";
import { CitySuggestion, SimpleLocation } from "../../../../platform-pages/src/lib/api/google-geocoding/google-geocoding.types";
import { SalaryExpectation } from "../salary-expectation-input/salary-expectation.type";
import { GoogleGeocodingService } from "../../../../platform-pages/src/lib/api/google-geocoding/google-geocoding.service";
import { CmsCachedService } from "../../../../platform-pages/src/lib/api/cms/cms-cached.service";
import { CmsNameValue, compareCmsNameValue } from "../../../../platform-pages/src/lib/api/cms/cms.types";

@Component({
  selector: "mh-location-input",
  templateUrl: "./location-input.component.html",
  styleUrls: ["./location-input.component.less"],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => LocationInputComponent), multi: true },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: LocationInputComponent,
    },
  ],
})
export class LocationInputComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() nzSize: "large" | "default" | "small" = "default";
  @Input() validators: ValidatorFn | null | undefined;
  @Input() groupValidators: ValidatorFn | null | undefined;
  @Input() groupError = false;
  @Input() distanceInput = false;
  @Input() zipOnly = false;
  @Input() required = false;
  @Input() set mark(isTouched: boolean) {
    if (isTouched) {
      Object.values(this.controlGroup.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }
  @Input() showLabels = false;
  @Input()
  set value(value: SimpleLocation) {
    this._value = value;
    this.controlGroup?.patchValue(value, { emitEvent: false });
    if (value?.city) {
      this.cityOptions = [{ placeId: value.geocoderResult || "", city: value.city, label: value.formattedAddress || value.city }];
    }
    this.zipOptions = value?.postalCode ? [value] : [];
    this.selectedCountryCode = value?.countryCode;
    this.location = value;
  }
  get value(): SimpleLocation {
    return this._value;
  }
  private _value!: SimpleLocation;
  disabled = false;

  public cityOptions: CitySuggestion[] = [];
  public zipOptions: SimpleLocation[] = [];
  private location: SimpleLocation = {};
  private citySearch$ = new Subject<string>();
  private zipSearch$ = new Subject<string>();
  private selectedCountryCode?: string;
  private suppressLocationSelection = false;
  zipFocused = false;
  private zipBlurTimeout: ReturnType<typeof setTimeout> | undefined;
  controlGroup!: FormGroup;
  public radiusOptions$: Observable<Array<CmsNameValue>>;
  private destroy$ = new Subject<void>();
  public zipSpan = 6;
  public loading = false
  public cityLoading = false;
  public zipLoading = false;
  public nzFilterOption = (): boolean => true;

  get countryControl(): AbstractControl | null {
    return this.controlGroup.get("country");
  }

  constructor(
    private fb: FormBuilder,
    private geocodingService: GoogleGeocodingService,
    private cmsCachedService: CmsCachedService,
  ) {
    this.radiusOptions$ = this.cmsCachedService.getUserDataByName("radius", false);
  }

  ngOnInit() {
    const currentValue: SimpleLocation = this.value ? this.value : this.getEmpty();

    this.controlGroup = this.fb.group({
      postalCode: [currentValue.postalCode, []],
      city: [currentValue.city, []],
      country: [currentValue.country, []],
      radius: [currentValue.radius, []],
    });

    if (this.groupValidators) {
      this.controlGroup.setValidators(this.groupValidators);
    }

    this.controlGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.onChange({ ...this.location, ...this.controlGroup.getRawValue() });
    });

    this.controlGroup.get("postalCode")?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((postalCode) => {
      if (postalCode) {
        this.onZipSelected(postalCode);
      } else if (!this.zipOnly) {
        this.clearLocationFromZip();
      }
    });
    this.controlGroup.get("city")?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((city) => {
      if (city) {
        this.onCitySelected(city);
      } else if (!this.zipOnly) {
        this.clearLocationFromCity();
      }
    });

    this.controlGroup.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(500)).subscribe((value) => {
      this.handleZipChange();
    });

    this.citySearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query) => {
        if (!query || query.trim().length < 2) return of(null);
        this.cityLoading = true;
        return this.geocodingService.searchCities(query, this.selectedCountryCode).pipe(catchError(() => of([])));
      }),
      takeUntil(this.destroy$),
    ).subscribe((location) => {
      this.cityOptions = location || [];
      this.cityLoading = false;
    });

    this.zipSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query) => {
        if (!query || query.trim().length < 2) return of([]);
        this.zipLoading = true;
        return this.geocodingService.decodeAddresses(query).pipe(catchError(() => of([])));
      }),
      takeUntil(this.destroy$),
    ).subscribe((locations) => {
      this.zipOptions = this.uniqueZipOptions(locations);
      this.zipLoading = false;
    });

    this.zipSpan = this.calculateZipSpan();
  }

  searchCity(query: string): void {
    this.citySearch$.next(query);
  }

  searchZip(query: string): void {
    const country = this.countryControl?.value;
    const city = this.controlGroup?.get("city")?.value;
    const context = [city, country].filter(Boolean).join(", ");
    this.zipSearch$.next(context ? `${query}, ${context}` : query);
  }

  onCitySelected(city: string): void {
    if (this.suppressLocationSelection) return;
    const selectedCity = this.cityOptions.find((option) => option.city === city);
    if (!selectedCity) return;

    this.cityLoading = true;
    this.geocodingService.decodeAddresses(selectedCity.label).pipe(
      catchError(() => of(null)),
      takeUntil(this.destroy$),
    ).subscribe((locations) => {
      if (!locations?.length) {
        const country = selectedCity.country || selectedCity.label.split(",").pop()?.trim();
        this.suppressLocationSelection = true;
        this.controlGroup.patchValue({ city: selectedCity.city, country }, { emitEvent: false });
        this.suppressLocationSelection = false;
        this.markLocationControlsClean();
        this.cityLoading = false;
        return;
      }

      const location = locations.find((item) => !!item.country) || locations[0];
      const country = location.country || selectedCity.country || selectedCity.label.split(",").pop()?.trim();
      this.location = location;
      this.selectedCountryCode = location.countryCode;
      this.zipOptions = this.uniqueZipOptions(locations);
      this.suppressLocationSelection = true;
      this.controlGroup.patchValue({
        city: location.city || selectedCity.city,
        postalCode: undefined,
        country,
      }, { emitEvent: false });
      this.markLocationControlsClean();
      this.suppressLocationSelection = false;
      this.cityOptions = [{
        ...selectedCity,
        city: location.city || selectedCity.city,
      }];
      this.onChange({ ...location, country, ...this.controlGroup.getRawValue() });
      this.cityLoading = false;
    });
  }

  onZipSelected(postalCode: string): void {
    const selectedZip = this.zipOptions.find((option) => option.postalCode === postalCode);
    if (!selectedZip) return;

    this.selectedCountryCode = selectedZip.countryCode;
    this.location = selectedZip;
    this.cityOptions = selectedZip.city
      ? [{ placeId: selectedZip.geocoderResult || "", city: selectedZip.city, label: selectedZip.formattedAddress || selectedZip.city }]
      : [];
    this.suppressLocationSelection = true;
    this.controlGroup.patchValue({
      postalCode: selectedZip.postalCode,
      city: selectedZip.city,
      country: selectedZip.country,
    }, { emitEvent: false });
    this.markLocationControlsClean();
    this.suppressLocationSelection = false;
    this.onChange({ ...selectedZip, ...this.controlGroup.getRawValue() });
  }

  private clearLocationFromZip(): void {
    this.suppressLocationSelection = true;
    this.controlGroup.patchValue({ city: undefined, country: undefined }, { emitEvent: false });
    this.suppressLocationSelection = false;
    this.location = {};
    this.cityOptions = [];
    this.zipOptions = [];
    this.selectedCountryCode = undefined;
    this.onChange({ ...this.controlGroup.getRawValue() });
  }

  private clearLocationFromCity(): void {
    this.suppressLocationSelection = true;
    this.controlGroup.patchValue({ postalCode: undefined, country: undefined }, { emitEvent: false });
    this.suppressLocationSelection = false;
    this.location = {};
    this.cityOptions = [];
    this.zipOptions = [];
    this.selectedCountryCode = undefined;
    this.onChange({ ...this.controlGroup.getRawValue() });
  }

  private markLocationControlsClean(): void {
    ["city", "country"].forEach((controlName) => {
      const control = this.controlGroup.get(controlName);
      control?.markAsPristine();
      control?.markAsUntouched();
      control?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      if (control?.value) control.setErrors(null);
    });
  }

  private uniqueZipOptions(locations: SimpleLocation[]): SimpleLocation[] {
    return locations.reduce((options, location) => {
      if (!location.postalCode) return options;
      const existingIndex = options.findIndex((option) => option.postalCode === location.postalCode);
      if (existingIndex < 0) return [...options, location];
      const existing = options[existingIndex];
      const existingScore = Number(!!existing.city) + Number(!!existing.country);
      const locationScore = Number(!!location.city) + Number(!!location.country);
      if (locationScore > existingScore) options[existingIndex] = location;
      return options;
    }, [] as SimpleLocation[]);
  }

  onCountryCodeSelected(countryCode: string): void {
    if (this.suppressLocationSelection) return;
    this.selectedCountryCode = countryCode;
    this.cityOptions = [];
    this.zipOptions = [];
    this.controlGroup?.patchValue({ city: undefined, postalCode: undefined }, { emitEvent: false });
    this.location = {};
    this.onChange({
      ...this.location,
      ...this.controlGroup?.getRawValue(),
      country: countryCode ? this.countryControl?.value : undefined,
      countryCode: countryCode || undefined,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleZipChange() {
    const zipControl = this.controlGroup.get("postalCode");
    const cityControl = this.controlGroup.get("city");
    const countryControl = this.countryControl;
    if (zipControl?.valid && this.zipFocused) {
      this.loading = true;
      this.geocodingService.decodeAddress(zipControl.value).subscribe((location) => {
        countryControl?.patchValue(location.country, { emitEvent: false });
        cityControl?.patchValue(location.city, { emitEvent: false });
        this.selectedCountryCode = location.countryCode;
        this.zipOptions = location.postalCode ? [location] : [];
        this.cityOptions = location.city
          ? [{ placeId: location.geocoderResult || "", city: location.city, label: location.formattedAddress || location.city }]
          : [];
        this.location = location;
        this.onChange({ ...location, ...this.controlGroup.getRawValue() });
        this.loading = false;
      });
    }
    this.onChange({ ...this.location, ...this.controlGroup.getRawValue() });
  }

  private getEmpty(): SimpleLocation {
    return {
      postalCode: undefined,
      country: undefined,
      city: undefined,
    };
  }

  onChange = (value: SalaryExpectation) => {};
  onTouched = () => {};

  writeValue(value: SimpleLocation) {
    this.value = value;
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    if(isDisabled) {
      this.controlGroup.get('postalCode')?.disable({onlySelf: true, emitEvent: false});
      this.controlGroup.get('city')?.disable({onlySelf: true, emitEvent: false});
      this.controlGroup.get('country')?.disable({onlySelf: true, emitEvent: false});
      this.controlGroup.get('radius')?.disable({onlySelf: true, emitEvent: false});
    } else {
      this.controlGroup.get('postalCode')?.enable({onlySelf: true, emitEvent: false});
      this.controlGroup.get('city')?.enable({onlySelf: true, emitEvent: false});
      this.controlGroup.get('country')?.enable({onlySelf: true, emitEvent: false});
      this.controlGroup.get('radius')?.enable({onlySelf: true, emitEvent: false});
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.controlGroup) return null;
    const value = this.controlGroup.getRawValue() as SimpleLocation;
    const hasAnyValue = !!(value.postalCode || value.city || value.country);
    if (!hasAnyValue) return this.required ? { required: true } : null;
    if (this.zipOnly) return value.country ? null : { zip: true };
    if (!value.city || !value.country) return { location: true };
    return this.controlGroup.errors;
  }

  handleZipBlur() {
    this.zipBlurTimeout = setTimeout(() => {
      this.zipFocused = false;
    }, 1000);
  }

  handleZipFocus() {
    this.zipFocused = true;
    if (this.zipBlurTimeout) {
      clearTimeout(this.zipBlurTimeout);
      this.zipBlurTimeout = undefined;
    }
  }

  calculateZipSpan(): number {
    if (this.zipOnly && this.distanceInput) {
      return 12;
    } else if (this.zipOnly) {
      return 24;
    } else if (this.distanceInput) {
      return 4;
    } else {
      return 6;
    }
  }

  protected readonly compareCmsNameValue = compareCmsNameValue;
}
